import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import { ORDER_METHODS, PaymentGateway, PaymentMethod, PaypalVariant } from '../../services/exports/Constants';
import { chargesStripeDestination } from '../../utils/stripe';

/**
 * Top-level PayPal router. There is a single "PayPal" entry in the checkout UI;
 * this decides which implementation backs it:
 *
 *  - `direct`     → PayPal-direct (paypal_standalone): money flows straight to the
 *                   restaurant's own PayPal. Chosen when it's set up AND the order allows it.
 *  - `via_spreedly` → PayPal through the partner's own PayPal or Braintree connection, on a
 *                   restaurant that charges through Spreedly. The backend lists `paypal` for such a
 *                   restaurant only when one of those connections serves it — gift cards included.
 *  - `via_stripe` → PayPal through Stripe (current production behaviour). Never for a restaurant on
 *                   Stripe destination charges: PayPal there would be a platform charge with no
 *                   restaurant behind it. The backend leaves `paypal` out for it already.
 *  - `null`       → no PayPal at all.
 *
 * We can only route OUT of PayPal-direct when the order carries a fee we can neither
 * collect nor avoid — both order-level facts, not company config:
 *  - external delivery (we must collect the payment to settle the courier bill), and
 *  - purchasing a gift card.
 * So pickup and internal/own delivery get direct; external delivery and gift-card
 * purchases fall back to via-Stripe. (Applying a gift card to a normal order is fine.)
 */
/**
 * @param isGiftCardCheckout Authoritative gift-card-purchase signal owned by
 *   PaymentFlowProvider (set by the gift-card checkout's pay button) — more reliable
 *   than inspecting the gift-card basket item.
 * @param unavailablePaymentMethods Methods the charging gateway refused this session
 *   (PaymentFlowProvider.markPaymentMethodUnavailable). PayPal among them withdraws the
 *   gateway-backed variants only: PayPal-direct never touches the gateway.
 */
export default function usePaypalVariant(
  isGiftCardCheckout: boolean,
  unavailablePaymentMethods: readonly PaymentMethod[] = [],
): PaypalVariant | null {
  const { company } = useSelector((state: StoreInterface) => state.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);

  // External delivery is identified by the order carrying an external delivery quote;
  // pickup and own/standalone delivery never have one.
  const isExternalDelivery = cached_order?.method === ORDER_METHODS.DELIVERY && Boolean(cached_order?.delivery_quote);

  const directAvailable = Boolean(company?.paypal_standalone?.available && company?.paypal_standalone?.client_id);
  const paypalSupported =
    Boolean(company?.supported_payment_methods?.includes(PaymentMethod.Paypal)) &&
    !unavailablePaymentMethods.includes(PaymentMethod.Paypal);
  const chargesThroughSpreedly = company?.payment_gateways?.[0] === PaymentGateway.Spreedly;

  if (directAvailable && !isExternalDelivery && !isGiftCardCheckout) {
    return PaypalVariant.Direct;
  }

  if (chargesThroughSpreedly) {
    return paypalSupported ? PaypalVariant.ViaSpreedly : null;
  }

  if (paypalSupported && !chargesStripeDestination(company)) {
    return PaypalVariant.ViaStripe;
  }

  return null;
}
